import { useQueryClient } from '@tanstack/react-query';
import { StyleSheet } from 'aphrodite';
import { useEffect, useId, useRef } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';
import { toast } from 'sonner';
import { StickToBottomContext } from 'use-stick-to-bottom';
import { CustomerAssistantContent } from '../../../admin/containers/AccountCustomerAssistant/CustomerAssistantUI';
import { AIChatMessage, AIChatRequest, AIChatRole, AIMessageRequest } from '../../../extViewmodels/sdk/models/AI';
import { useStreamingResponseMutation } from '../../../hooks/useStreamingResponseMutation';
import { AIConversationMessage } from '../../../web/components-v2/ai/AIConversationMessage';
import {
	ChatConversation,
	ChatConversationContent,
	ChatConversationScrollButton,
} from '../../../web/components-v2/ai/ChatConversation';
import {
	PromptInput,
	PromptInputBody,
	PromptInputFooter,
	PromptInputMessage,
	PromptInputSpeechButton,
	PromptInputSubmit,
	PromptInputTextarea,
	PromptInputTools,
} from '../../../web/components-v2/ai/PromptInput';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { AIChatAssistantMessage } from '../../../web/containers/templates/AssistantChat';
import { syncAIStreamToRQCache } from '../../../web/containers/templates/AssistantChat/utils';
import { white } from '../../../web/styles/colors';
import { bs } from '../../../web/styles/styles';
import { BASE_URL } from '../../constants';
import { useIsCustomerAssistantEmbedMode } from '../../hooks/useIsCustomerAssistantEmbedMode';
import {
	getPublicConversationChatMessageQueryKey,
	usePublicAIConversationChatMessageQuery,
} from '../../queries/usePublicAIConversationChatMessageQuery';
import {
	getParentOriginFromSearch,
	LEV_ASSISTANT_CONVERSATION_UPDATED_MESSAGE,
	LEV_ASSISTANT_READY_MESSAGE,
	postMessageToParent,
} from '../../utils/embedMessaging';
import { CustomerAssistantLayout } from './CustomerAssistantLayout';
import { MeetingSchedulingWidget } from './MeetingSchedulingWidget';

export function CustomerAssistantChatPage() {
	const id = useId();
	const history = useHistory();
	const queryClient = useQueryClient();
	const location = useLocation<{ starterMessages: AIChatMessage[] }>();
	const isEmbedMode = useIsCustomerAssistantEmbedMode();
	const parentOrigin = getParentOriginFromSearch(location.search);
	const { conversationId } = useParams<{ conversationId: string }>();
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const conversationRef = useRef<StickToBottomContext>(null);
	const aiConversationChatMessageQuery = usePublicAIConversationChatMessageQuery({
		id: conversationId,
		initialData: location.state?.starterMessages,
		staleTime: 1000, // to prevent overwriting initialData with an empty array from the server
	});
	const aiPromptMutation = useStreamingResponseMutation({
		mutationFn: (params: { conversationId: string; chatRequest: AIChatRequest }) =>
			fetch(`${BASE_URL}/public/aiConversation/${conversationId}/stream`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(params.chatRequest),
			}),
		onEvent: (event, params) => {
			syncAIStreamToRQCache({
				event,
				queryClient,
				queryKey: getPublicConversationChatMessageQueryKey({
					id: conversationId,
				}),
				chatRequest: params.chatRequest,
			});
		},
		onError: _error => {
			toast.error('Error while streaming AI assistant response');
		},
	});
	const sendPrompt = aiPromptMutation.mutate;

	const handlePromptSubmit = ({ text }: PromptInputMessage) => {
		conversationRef.current?.scrollToBottom();
		queryClient.setQueryData(
			getPublicConversationChatMessageQueryKey({ id: conversationId }),
			(prevMessages: AIChatMessage[]) => [...(prevMessages ?? []), { role: AIChatRole.User, text, id: null }]
		);
		sendPrompt({
			conversationId,
			chatRequest: {
				chatMessages: [{ role: AIChatRole.User, text, id: null }],
			} as AIMessageRequest,
		});
	};

	const handleMeetingTimeSelected = (timeSelectionText: string) => {
		handlePromptSubmit({ text: timeSelectionText, files: [] });
	};

	useEffect(() => {
		if (!isEmbedMode) {
			return;
		}

		postMessageToParent({
			type: LEV_ASSISTANT_READY_MESSAGE,
			targetOrigin: parentOrigin,
		});
	}, [isEmbedMode, parentOrigin]);

	useEffect(() => {
		if (!isEmbedMode || !conversationId) {
			return;
		}

		postMessageToParent({
			type: LEV_ASSISTANT_CONVERSATION_UPDATED_MESSAGE,
			payload: { conversationId },
			targetOrigin: parentOrigin,
		});
	}, [conversationId, isEmbedMode, parentOrigin]);

	useEffect(() => {
		const starterMessages = location.state?.starterMessages;
		if (starterMessages == null) {
			return;
		}
		sendPrompt({
			conversationId,
			chatRequest: {
				chatMessages: starterMessages,
			} as AIMessageRequest,
		});
		// Clear the starter messages from the location state so they don't get re-sent on back navigation
		history.replace({ pathname: location.pathname, search: location.search });
	}, [conversationId, location.pathname, location.search, location.state?.starterMessages, sendPrompt, history]);

	return (
		<CustomerAssistantLayout>
			<CustomerAssistantContent styles={[s.content]}>
				<ChatConversation styles={[s.conversation]} contextRef={conversationRef}>
					<ChatConversationContent>
						{aiConversationChatMessageQuery.data?.map((message, i) => (
							<AIConversationMessage
								key={`${id}-message-${message.id ?? i}`}
								conversationId={conversationId}
								message={message}
								showMessageToolbar={false}
								renderMeetingSchedulingWidget={toolCall => (
									<MeetingSchedulingWidget toolCall={toolCall} onTimeSlotSelected={handleMeetingTimeSelected} />
								)}
							/>
						))}
						{aiPromptMutation.isLoading ? (
							<AIChatAssistantMessage>
								<LoadingSpinner type='tiny' />
							</AIChatAssistantMessage>
						) : null}
					</ChatConversationContent>
					<ChatConversationScrollButton />
				</ChatConversation>
				<PromptInput styles={[bs.mt4]} onSubmit={handlePromptSubmit}>
					<PromptInputBody>
						<PromptInputTextarea ref={textareaRef} />
					</PromptInputBody>
					<PromptInputFooter>
						<PromptInputTools>
							{!isEmbedMode ? <PromptInputSpeechButton textareaRef={textareaRef} /> : null}
						</PromptInputTools>
						<PromptInputSubmit status={aiPromptMutation.status} />
					</PromptInputFooter>
				</PromptInput>
			</CustomerAssistantContent>
		</CustomerAssistantLayout>
	);
}

const s = StyleSheet.create({
	content: {
		alignItems: 'flex-start',
		minHeight: 0,
	},
	conversation: {
		flex: 1,
		minHeight: 0,
	},
	inputContainer: {
		padding: '16px 24px',
		borderTop: '1px solid #e9ebef',
	},
	chatInput: {
		background: white,
		width: '100%',
	},
});
